import type { Locale } from '@/types/company';

export const USER_ROLE = {
  ADMINS: 'admins',
  TEAMLEADS: 'teamleads',
  USERS: 'users',
} as const;

type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

export type FeaturePermission = {
  approvals: number;
  comp_cycle: number;
  market_pulse: number;
  people_access: number | null;
  ranges_access: number | null;
  approvals_attrs?: {
    approver_substitute_id?: number;
    submitter_substitute_id?: number;
  };
};

type AppAccess = {
  nav: {
    comp_cycle: boolean;
    people: boolean;
    ranges: boolean;
    approvals: boolean;
    integrations: boolean;
    market_pulse: boolean;
    market_data: boolean;
    my_portal: boolean;
    insights: boolean;
    data: boolean;
    ai_assistant: boolean;
  };
  home: {
    create_team_resources: boolean;
    show_team_resources: boolean;
    show_invite_team_members: boolean;
    onboarding: boolean;
  };
  settings: {
    company: {
      general: boolean;
      team_members: boolean;
      job_architecture: boolean;
      security: boolean;
      api: boolean;
    };
    show_footer: boolean;
    show_user_management_actions: boolean;
  };
  people: {
    summary: {
      view: boolean;
    };
    list: {
      view: boolean;
      view_employee_portal: boolean;
      view_managers: boolean;
      add_employees: boolean;
      export_employees: boolean;
      edit_employees: boolean;
      delete_employees: boolean;
    };
    edit_columns: {
      disable_adp_columns: boolean;
    };
    settings: boolean;
    show_employee_portal_settings: boolean;
    show_managers_settings: boolean;
  };
  ranges: {
    list: {
      view: boolean;
    };
    settings: {
      view: boolean;
    };
  };
  approvals: {
    add: boolean;
    settings: boolean;
    debug: boolean;
    export: boolean;
  };
  ccp: {
    show_pay_element_code: boolean;
    show_export_to_hris: boolean;
  };
};
export type UserMinimal = {
  id: number;
  full_name: string;
};

export type UserProfile = {
  email: string;
  avatar: string;
  full_name: string;
  first_name: string;
  last_name: string;
  short_name: string;
  registered_at: string;
  id: number;
  default_approver: boolean;
  is_admin: boolean;
  is_ccp_user: boolean;
  is_onboarding: boolean;
  is_teamlead: boolean;
  is_staff?: boolean;
  has_people_access: boolean;
  employee_v2_id: number;
  feature_permission: FeaturePermission;
  company_schema_name: string;
  visible_ranges_count: number;
  visible_comp_cycles_count: number;
  visible_subordinates_count: number;
  app_access: AppAccess;
  role_name: UserRole;
  has_been_invited: boolean;
  staff_mode: boolean;
  has_accepted_terms: boolean;
  locale: Locale | null;
  rollout_flags: Record<string, boolean>;
  is_temp_password: boolean;
  is_power_user: boolean;
};

export type LoginPayload = {
  email: string;
  credential: string;
  first_name?: string;
  last_name?: string;
};

export type InvitationLoginPayload = {
  first_name: string;
  last_name: string;
  password1: string;
  password2: string;
  locale: Locale | null;
};

export type UpdateUserPayload = {
  first_name: string;
  last_name: string;
  email: string;
};

export type InviteUserPayload = {
  email: string;
  first_name: string;
  last_name: string;
};

export type PeopleAccessEmployee = {
  id: number;
  full_name: string;
  eid: string;
  email: string;
};

export type PeopleAccessUser = {
  id: number;
  full_name: string;
};

export type PeopleAccess = {
  id: number;
  name: string;
  access_type: string;
  employees: PeopleAccessEmployee[];
  columns: string[];
  employee_portal_access: boolean;
};

export type PeopleAccessColumn = {
  name: string;
  display_name: string;
  required: boolean;
  filterable: boolean;
};

export type PeopleAccessForm = {
  access_type: string;
  view_as?: number[];
  employees: number[];
  columns: string[];
  name: string;
  id?: number;
  employee_portal_access: boolean;
};

export type Invitation = {
  error?: string;
  detail: string;
  active: boolean;
  domain: string;
  sender_email?: string;
  company_name: string;
  user: UserProfile;
};

export type UserPatchPayload = Partial<{
  first_name: string;
  last_name: string;
  email: string;
  group: string;
  locale: Locale | null;
}>;
